/**
 * Shared chrome for every page under /staff/*.
 *
 * Renders the top header (logo, STAFF chip, nav: Triggers / Pulse /
 * Settings, user email, sign out) and a content slot. Active nav link
 * is highlighted off useLocation so the operator always knows where
 * they are.
 *
 * Use this on every new staff page so the cross-module nav is always
 * one click away - the alternative (browser back) loses context and
 * was an explicit UX gap on the Pulse page.
 */

import { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { LogOut } from 'lucide-react'

import { headlessLogout } from '@/shared/lib/auth-api'
import { useSession } from '@/admin/hooks/use-session'
import { Button } from '@/shared/components/ui/button'
import { Logo } from '@/shared/components/ui/logo'
import { toastApiError } from '@/shared/lib/api-error'
import { cn } from '@/shared/lib/utils'

interface NavItem {
  label: string
  href: string
  matchPrefix: string
}

export function StaffShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { data: session } = useSession()
  const user = session?.user

  async function handleLogout() {
    try {
      await headlessLogout()
      await queryClient.invalidateQueries({ queryKey: ['session'] })
      navigate('/staff/login')
    } catch (err) {
      toastApiError(err, 'Logout failed')
    }
  }

  const navItems: NavItem[] = [
    { label: 'Dashboard', href: '/staff/dashboard', matchPrefix: '/staff/dashboard' },
    { label: 'Voice Calls', href: '/staff/voice-calls', matchPrefix: '/staff/voice-calls' },
    {
      label: 'Comm Logs',
      href: '/staff/communication-logs',
      matchPrefix: '/staff/communication-logs',
    },
    {
      label: 'Email Templates',
      href: '/staff/email-templates',
      matchPrefix: '/staff/email-templates',
    },
    { label: 'Triggers', href: '/staff/triggers/diagnose', matchPrefix: '/staff/triggers' },
    { label: 'Pulse', href: '/staff/pulse', matchPrefix: '/staff/pulse' },
    { label: 'Redis', href: '/staff/cache', matchPrefix: '/staff/cache' },
    {
      label: t('nav.settings'),
      href: '/staff/settings/translations',
      matchPrefix: '/staff/settings',
    },
  ]

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header className="flex h-14 items-center justify-between border-b-[0.5px] border-[var(--rule)] px-6">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/staff/dashboard')}
            className="cursor-pointer"
            aria-label="Staff dashboard"
          >
            <Logo size="sm" />
          </button>
          <span className="rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--green)]">
            {t('common.staff')}
          </span>
          <nav className="ml-2 flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = location.pathname.startsWith(item.matchPrefix)
              return (
                <Button
                  key={item.href}
                  variant="ghost"
                  onClick={() => navigate(item.href)}
                  className={cn(
                    'clara-section-label rounded-[4px] px-3 py-1.5 tracking-widest transition-colors',
                    isActive
                      ? 'bg-[var(--bg-subtle)] text-[var(--ink)]'
                      : 'text-[var(--ink-3)] hover:bg-[var(--bg-subtle)]',
                  )}
                >
                  {item.label}
                </Button>
              )
            })}
          </nav>
        </div>
        <div className="flex items-center gap-1">
          <span className="clara-small mr-3 hidden sm:inline">{user?.email}</span>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="flex cursor-pointer items-center gap-1.5 rounded-[4px] px-3 py-1.5 text-[11px] text-[var(--ink-3)] transition-all duration-150 hover:bg-[var(--bg-subtle)]"
          >
            <LogOut className="h-3.5 w-3.5" />
            {t('common.signOut')}
          </Button>
        </div>
      </header>
      <main>{children}</main>
    </div>
  )
}
